import React, { useState, useEffect } from "react";
import { Modal, Box, TextField, Button, CircularProgress } from "@mui/material";
import { CrmMainService } from "../../../../service/manage/crm/crm_main.service";
import { sweetStatusAlert } from "../../../../components/common/SweetAlert";
import SimpleReactValidator from "simple-react-validator";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import { green, red } from "@mui/material/colors";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  zIndex: 1300,
};

const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1400,
};

const CrmSearchModal = ({
  isOpen,
  onClose,
  state,
  filters,
  resetFilters,
  fetchSavedFilters,
  intermediateFilters,
}) => {
  const [filterShortName, setFilterShortName] = useState("");
  const [filterLongName, setFilterLongName] = useState("");
  const [loading, setLoading] = useState(false);
  const [, forceUpdate] = useState({});
  const [selectedStatus, setSelectedStatus] = useState(1);

  const statuses = [
    {
      id: 1,
      name: "Restricted View",
      icon: <LockIcon fontSize="small" />,
      color: red[500],
      bgColor: red[50],
    },
    {
      id: 2,
      name: "Public View",
      icon: <PublicIcon fontSize="small" />,
      color: green[600],
      bgColor: green[50],
    },
  ];

  const [validator] = useState(() => {
    const validator = new SimpleReactValidator({
      className: "text-danger",
      messages: {
        required: "Filter Title is required.",
        max: "Filter Title must not exceed :max characters.",
      },
    });
    return validator;
  });

  const handleSaveFilter = async () => {
    if (!validator.allValid()) {
      validator.showMessages();
      forceUpdate({});
      return;
    }

    try {
      setLoading(true);

      let search_array;

      if (intermediateFilters.length > 0) {
        search_array = intermediateFilters.map((filter) => ({
          ID: filter.id,
        }));
      } else {
        // Fresh save logic
        search_array = filters.map((fil, index) => {
          const isDropdownValue = fil.searchValueOptions !== null;
          const isDateValue = fil.isCalendar;

          const searchValue = isDropdownValue
            ? Number(fil.searchValue)
            : isDateValue
            ? fil.searchValue
            : fil.searchValue;

          const obj = {
            37: Number(fil.operation),
            39: Number(fil.comparisonType),
            FLD: Number(fil.blockText),
            VAL: searchValue,
          };

          if (index !== 0) {
            obj[38] = Number(fil.condition);
          }

          return obj;
        });
      }

      const filterObject = {
        node_mid: state.mid,
        search_id: null,
        search_array: search_array,
        user_id: state.lg_user_id,
        user_table_id: state.lg_user_table_id,
        create_user_id: state.lg_user_id,
        create_user_table_id: state.lg_user_table_id,
        search_title: filterShortName,
        note: filterLongName,
        status_search: selectedStatus,
      };

      handleCloseModal();
      const response = await CrmMainService.crm_search_criteria(filterObject);

      if (response.success) {
        sweetStatusAlert("Success", "Filter saved successfully!", "success");
        setFilterShortName("");
        setFilterLongName("");
        resetFilters();
        await fetchSavedFilters();
      } else {
        sweetStatusAlert(
          "Failed",
          "Failed to save filter. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error saving filter:", error);
      sweetStatusAlert(
        "Failed",
        "An error occurred while saving the filter.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    validator.hideMessages();
    forceUpdate({});

    onClose();
  };

  return (
    <>
      <Modal open={isOpen} onClose={handleCloseModal}>
        <Box sx={modalStyle}>
          <h2>Save Filter</h2>
          <h6>(Note: only completed rows will be saved!)</h6>
          <TextField
            fullWidth
            label="Filter Title (Add a unique title)"
            variant="outlined"
            value={filterShortName}
            onChange={(e) => {
              setFilterShortName(e.target.value);
              validator.showMessageFor("filterShortName");
              forceUpdate({});
            }}
            onBlur={() => {
              validator.showMessageFor("filterShortName");
              forceUpdate({});
            }}
            error={
              !!validator.message(
                "filterShortName",
                filterShortName,
                "required|max:100"
              )
            }
            helperText={validator.message(
              "filterShortName",
              filterShortName,
              "required|max:100"
            )}
            margin="normal"
          />

          <TextField
            fullWidth
            label="Filter Note"
            variant="outlined"
            value={filterLongName}
            onChange={(e) => setFilterLongName(e.target.value)}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="status-select-label">View Status</InputLabel>
            <Select
              labelId="status-select-label"
              id="status-select"
              value={selectedStatus}
              label="View Status"
              onChange={(e) => setSelectedStatus(e.target.value)}
              sx={{
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              {statuses.map((option) => (
                <MenuItem
                  key={option.id}
                  value={option.id}
                  sx={{
                    color: option.color,
                    backgroundColor:
                      option.id === selectedStatus ? option.bgColor : "inherit",
                    "&:hover": {
                      backgroundColor: option.bgColor,
                      opacity: 0.8,
                    },
                    "&.Mui-selected": {
                      backgroundColor: option.bgColor,
                      "&:hover": {
                        backgroundColor: option.bgColor,
                      },
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    {option.icon}
                    {option.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box display="flex" justifyContent="space-between" mt={2}>
            <button className="filtbtn fil-cancel" onClick={handleCloseModal}>
              Cancel
            </button>

            <button className="filtbtn fil-save" onClick={handleSaveFilter}>
              Save
            </button>
          </Box>
        </Box>
      </Modal>

      {loading && (
        <Box sx={overlayStyle}>
          <CircularProgress size={60} color="primary" />
        </Box>
      )}
    </>
  );
};

export default CrmSearchModal;
